import React, { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import dagre from "@dagrejs/dagre";
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  ViewportPortal,
  getSmoothStepPath,
  useReactFlow,
  useViewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./styles.css";

const statusOrder = ["documented", "family", "strong", "weak", "excluded"];
const statusStyle = {
  documented: { label: "Документ", color: "#2d6a4f" },
  family: { label: "Семья", color: "#4f6193" },
  strong: { label: "Сильная гипотеза", color: "#8b5a12" },
  weak: { label: "Рабочая гипотеза", color: "#795548" },
  excluded: { label: "Исключено", color: "#8c4354" },
};
const branchPalette = ["#3f6b52", "#74558f", "#94603f", "#3f7180", "#5f6762", "#8a5b68", "#6c6b3f", "#536b8c"];
const NODE_WIDTH = 226;
const GROUP_WIDTH = 246;
const NODE_HEIGHT = 100;
const LATERAL_GAP = 28;
const FOCUS_NODE_WIDTH = 264;
const FOCUS_ROW_GAP = 54;
const FOCUS_FAMILY_GAP = 28;
const FOCUS_ROW_STEP = 250;
const HOME_ROW_STEP = 230;
const HOME_BLOCK_GAP = 28;
const HOME_PERSON_ANCHOR_WEIGHT = 100;
const DEFAULT_HOME_ANCESTOR_DEPTH = 4;
const MarkdownViewer = lazy(() => import("./MarkdownViewer.jsx"));

function branchColor(branch = "other") {
  let hash = 0;
  for (const character of branch) hash = ((hash << 5) - hash + character.codePointAt(0)) | 0;
  return branchPalette[Math.abs(hash) % branchPalette.length];
}

function localHref(localPath) {
  return `/api/source?path=${encodeURIComponent(localPath)}`;
}

function markdownHref(localPath, anchor, returnHref) {
  const parameters = new URLSearchParams({ path: localPath });
  if (returnHref) parameters.set("return", returnHref);
  return `/markdown?${parameters.toString()}${anchor ? `#${encodeURIComponent(anchor)}` : ""}`;
}

function sourceHref(source, returnHref) {
  if (/\.md$/i.test(source?.localPath || "")) return markdownHref(source.localPath, source.anchor, returnHref);
  return source?.url || (source?.localPath ? localHref(source.localPath) : null);
}

function MaterialIcon({ name, size = 20 }) {
  const paths = {
    search: <><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" /></>,
    refresh: <><path d="M20 6v5h-5" /><path d="M19 11a7.5 7.5 0 1 0 .2 5" /></>,
    logout: <><path d="M10 5H5v14h5" /><path d="M13 8l4 4-4 4" /><path d="M8 12h9" /></>,
    close: <><path d="m6 6 12 12" /><path d="M18 6 6 18" /></>,
    back: <><path d="m15 18-6-6 6-6" /></>,
    home: <><path d="m4 11 8-7 8 7" /><path d="M6.5 10v9h11v-9" /><path d="M10 19v-5h4v5" /></>,
    fit: <><path d="M8 4H4v4" /><path d="M16 4h4v4" /><path d="M20 16v4h-4" /><path d="M8 20H4v-4" /></>,
    expand: <><path d="m7 10 5 5 5-5" /></>,
    collapse: <><path d="m7 14 5-5 5 5" /></>,
  };
  return <svg className="material-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

const FamilyNode = memo(({ data, selected }) => (
  <div className={`family-node ${selected ? "is-selected" : ""} ${data.focused ? "is-focus" : ""} ${data.kind === "group" ? "is-group" : ""}`} style={{ "--branch": branchColor(data.branch) }}>
    <Handle id="top-target" type="target" position={Position.Top} className="node-handle" />
    <Handle id="left-target" type="target" position={Position.Left} className="node-handle lateral-handle" />
    <Handle id="left-source" type="source" position={Position.Left} className="node-handle lateral-handle" />
    <div className="node-name">{data.name}</div>
    {data.subtitle && <div className="node-subtitle">{data.subtitle}</div>}
    <div className="node-meta">
      <span>{data.contextRole || (data.kind === "group" ? "группа" : "человек")}</span>
      <span className={`tiny-status status-${data.status}`}>{statusStyle[data.status]?.label || data.status}</span>
    </div>
    <Handle id="right-target" type="target" position={Position.Right} className="node-handle lateral-handle" />
    <Handle id="right-source" type="source" position={Position.Right} className="node-handle lateral-handle" />
    <Handle id="bottom-source" type="source" position={Position.Bottom} className="node-handle" />
  </div>
));

function GenerationBands({ bands }) {
  const { x: viewportX, zoom } = useViewport();
  if (!bands.length) return null;
  const safeZoom = Math.max(zoom, 0.03);
  const labelScale = 1 / safeZoom;
  return (
    <ViewportPortal>
      <div className="generation-bands" aria-hidden="true">
        {bands.map((band, index) => (
          <div
            key={band.generation}
            className={`generation-band ${index % 2 ? "is-alternate" : ""}`}
            style={{ transform: `translate(${band.x}px, ${band.y}px)`, width: band.width, height: band.height }}
          >
            <span style={{ left: (16 - viewportX) / safeZoom - band.x, "--generation-label-scale": labelScale }}>{band.label}</span>
          </div>
        ))}
      </div>
    </ViewportPortal>
  );
}

const EvidenceEdge = memo((props) => {
  const [pathValue, labelX, labelY] = getSmoothStepPath({ ...props, borderRadius: 10, offset: 18 });
  const style = statusStyle[props.data.status] || statusStyle.family;
  return (
    <>
      <BaseEdge id={props.id} path={pathValue} markerEnd={props.markerEnd} style={{ stroke: style.color, strokeWidth: props.selected ? 3 : 1.7, strokeDasharray: ["strong", "weak", "excluded"].includes(props.data.status) ? "7 5" : undefined, opacity: props.data.status === "excluded" ? 0.55 : 0.88 }} />
      {props.selected && props.data.shortLabel && props.data.shortLabel !== "Родственная связь" && props.data.shortLabel !== "Связь / союз" && (
        <EdgeLabelRenderer>
          <button className="edge-label" style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, borderColor: style.color }} onClick={(event) => { event.stopPropagation(); props.data.onOpen(props.id); }}>
            {props.data.shortLabel}
          </button>
        </EdgeLabelRenderer>
      )}
    </>
  );
});

const nodeTypes = { family: FamilyNode };
const edgeTypes = { evidence: EvidenceEdge };

class DisjointSet {
  constructor(ids) { this.parent = new Map(ids.map((id) => [id, id])); }
  find(id) {
    const parent = this.parent.get(id);
    if (parent === id) return id;
    const root = this.find(parent);
    this.parent.set(id, root);
    return root;
  }
  union(left, right) {
    const a = this.find(left);
    const b = this.find(right);
    if (a !== b) this.parent.set(b, a);
  }
}

function orderLateralMembers(members, lateralRelationships) {
  if (members.length < 2) return members;
  const memberIds = new Set(members.map((person) => person.id));
  const degree = new Map(members.map((person) => [person.id, 0]));
  for (const relation of lateralRelationships) {
    if (!memberIds.has(relation.from) || !memberIds.has(relation.to)) continue;
    degree.set(relation.from, degree.get(relation.from) + 1);
    degree.set(relation.to, degree.get(relation.to) + 1);
  }
  return [...members].sort((left, right) => {
    const kindScore = (person) => person.kind === "group" ? 2 : 0;
    return kindScore(left) - kindScore(right) || degree.get(right.id) - degree.get(left.id) || left.name.localeCompare(right.name, "ru");
  });
}

function layoutGraph(people, relationships, { groupSiblingRelationships = true } = {}) {
  const peopleById = new Map(people.map((person) => [person.id, person]));
  const disjoint = new DisjointSet(people.map((person) => person.id));
  const lateralRelationships = relationships.filter((relation) => {
    if (!["partner", "sibling", "same-person"].includes(relation.type) || !peopleById.has(relation.from) || !peopleById.has(relation.to)) return false;
    const fromGeneration = peopleById.get(relation.from)?.generation;
    const toGeneration = peopleById.get(relation.to)?.generation;
    return fromGeneration === undefined || toGeneration === undefined || fromGeneration === toGeneration;
  });
  for (const relation of lateralRelationships) {
    if (relation.type !== "sibling" || groupSiblingRelationships) disjoint.union(relation.from, relation.to);
  }

  const families = new Map();
  for (const person of people) {
    const root = disjoint.find(person.id);
    if (!families.has(root)) families.set(root, []);
    families.get(root).push(person);
  }

  const graph = new dagre.graphlib.Graph();
  graph.setGraph({ rankdir: "TB", ranker: "network-simplex", align: "UL", ranksep: 120, nodesep: 54, edgesep: 28, marginx: 48, marginy: 48, acyclicer: "greedy" });
  graph.setDefaultEdgeLabel(() => ({}));

  for (const [familyId, rawMembers] of families) {
    const members = orderLateralMembers(rawMembers, lateralRelationships);
    families.set(familyId, members);
    const width = members.reduce((sum, person) => sum + (person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH), 0) + Math.max(0, members.length - 1) * LATERAL_GAP;
    graph.setNode(familyId, { width, height: NODE_HEIGHT });
  }

  const seenFamilyEdges = new Set();
  for (const relation of relationships) {
    if (relation.type !== "parent" || !peopleById.has(relation.from) || !peopleById.has(relation.to)) continue;
    const from = disjoint.find(relation.from);
    const to = disjoint.find(relation.to);
    if (from === to || seenFamilyEdges.has(`${from}>${to}`)) continue;
    seenFamilyEdges.add(`${from}>${to}`);
    graph.setEdge(from, to, { minlen: 1, weight: relation.status === "documented" ? 8 : relation.status === "family" ? 6 : 3 });
  }

  dagre.layout(graph);

  const result = [];
  for (const [familyId, members] of families) {
    const familyPosition = graph.node(familyId);
    const familyWidth = members.reduce((sum, person) => sum + (person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH), 0) + Math.max(0, members.length - 1) * LATERAL_GAP;
    let cursor = familyPosition.x - familyWidth / 2;
    for (const person of members) {
      const width = person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH;
      const lines = person.label.split("\n");
      result.push({
        id: person.id,
        type: "family",
        position: { x: cursor, y: familyPosition.y - NODE_HEIGHT / 2 },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        data: { ...person, subtitle: lines.slice(1, 3).join(" · ") },
      });
      cursor += width + LATERAL_GAP;
    }
  }
  return result;
}

function nodeCardWidth(node) {
  return node.data.kind === "group" ? GROUP_WIDTH : NODE_WIDTH;
}

// Project preferred block centers onto a non-overlapping row while preserving their order.
function packHomeRow(blocks, gap = HOME_BLOCK_GAP) {
  if (!blocks.length) return new Map();
  const ordered = [...blocks].sort((left, right) => left.target - right.target || left.baseCenter - right.baseCenter || left.id.localeCompare(right.id));
  let cumulativeOffset = 0;
  const entries = ordered.map((block, index) => {
    if (index > 0) cumulativeOffset += ordered[index - 1].width / 2 + gap + block.width / 2;
    return { block, offset: cumulativeOffset, preferred: block.target - cumulativeOffset, weight: Math.max(1, block.weight || 1) };
  });
  const pools = [];
  for (let index = 0; index < entries.length; index += 1) {
    pools.push({ start: index, end: index, weight: entries[index].weight, value: entries[index].preferred });
    while (pools.length > 1 && pools.at(-2).value > pools.at(-1).value) {
      const right = pools.pop();
      const left = pools.pop();
      const weight = left.weight + right.weight;
      pools.push({
        start: left.start,
        end: right.end,
        weight,
        value: (left.value * left.weight + right.value * right.weight) / weight,
      });
    }
  }
  const centers = new Map();
  for (const pool of pools) {
    for (let index = pool.start; index <= pool.end; index += 1) {
      centers.set(entries[index].block.id, pool.value + entries[index].offset);
    }
  }
  return centers;
}

// Place each ancestor row from the already positioned children, so nearby families stay compact
// and collisions are pushed progressively into the older generations.
function alignHomeFamilyRows(baseNodes, relationships, homeId) {
  const nodeById = new Map(baseNodes.map((node) => [node.id, node]));
  const homeNode = nodeById.get(homeId);
  if (!homeNode) return baseNodes;

  const disjoint = new DisjointSet(baseNodes.map((node) => node.id));
  const samePersonById = new Map();
  for (const relation of relationships) {
    const fromNode = nodeById.get(relation.from);
    const toNode = nodeById.get(relation.to);
    if (!fromNode || !toNode || fromNode.data.generation !== toNode.data.generation) continue;
    if (["partner", "same-person"].includes(relation.type)) disjoint.union(relation.from, relation.to);
    if (relation.type === "same-person") {
      if (!samePersonById.has(relation.from)) samePersonById.set(relation.from, new Set());
      if (!samePersonById.has(relation.to)) samePersonById.set(relation.to, new Set());
      samePersonById.get(relation.from).add(relation.to);
      samePersonById.get(relation.to).add(relation.from);
    }
  }

  const membersByBlock = new Map();
  for (const node of baseNodes) {
    const blockId = disjoint.find(node.id);
    if (!membersByBlock.has(blockId)) membersByBlock.set(blockId, []);
    membersByBlock.get(blockId).push(node);
  }
  const blocks = [...membersByBlock].map(([id, members]) => {
    const left = Math.min(...members.map((node) => node.position.x));
    const right = Math.max(...members.map((node) => node.position.x + nodeCardWidth(node)));
    const baseCenter = (left + right) / 2;
    return {
      id,
      members,
      generation: members[0].data.generation ?? 0,
      width: right - left,
      baseCenter,
      offsets: new Map(members.map((node) => [node.id, node.position.x + nodeCardWidth(node) / 2 - baseCenter])),
    };
  });
  const blockByPerson = new Map();
  for (const block of blocks) {
    for (const member of block.members) blockByPerson.set(member.id, block);
  }

  const homeIdentityIds = new Set();
  const identityQueue = [homeId];
  while (identityQueue.length) {
    const id = identityQueue.shift();
    if (homeIdentityIds.has(id)) continue;
    homeIdentityIds.add(id);
    for (const aliasId of samePersonById.get(id) || []) identityQueue.push(aliasId);
  }

  const parentRelationships = relationships.filter((relation) => {
    if (relation.type !== "parent") return false;
    const parentNode = nodeById.get(relation.from);
    const childNode = nodeById.get(relation.to);
    return parentNode && childNode && parentNode.data.generation + 1 === childNode.data.generation;
  });
  const blockCenters = new Map();
  const homeBlock = blockByPerson.get(homeId);
  const homeShift = -(homeBlock.baseCenter + homeBlock.offsets.get(homeId));
  for (const block of blocks) blockCenters.set(block.id, block.baseCenter + homeShift);
  const personCenter = (id) => {
    const block = blockByPerson.get(id);
    return block ? blockCenters.get(block.id) + block.offsets.get(id) : null;
  };

  const generations = [...new Set(blocks.map((block) => block.generation))];
  for (const generation of generations.filter((value) => value < 0).sort((left, right) => right - left)) {
    const row = blocks.filter((block) => block.generation === generation).map((block) => {
      let weightedTarget = 0;
      let totalWeight = 0;
      const memberIds = new Set(block.members.map((member) => member.id));
      for (const relation of parentRelationships) {
        if (!memberIds.has(relation.from)) continue;
        const childCenter = personCenter(relation.to);
        if (childCenter === null) continue;
        const weight = homeIdentityIds.has(relation.to) ? HOME_PERSON_ANCHOR_WEIGHT : 1;
        weightedTarget += childCenter * weight;
        totalWeight += weight;
      }
      return {
        ...block,
        target: totalWeight ? weightedTarget / totalWeight : block.baseCenter + homeShift,
        weight: totalWeight || 4,
      };
    });
    for (const [blockId, center] of packHomeRow(row)) blockCenters.set(blockId, center);
  }

  for (const generation of generations.filter((value) => value > 0).sort((left, right) => left - right)) {
    const row = blocks.filter((block) => block.generation === generation).map((block) => {
      let weightedTarget = 0;
      let totalWeight = 0;
      const memberIds = new Set(block.members.map((member) => member.id));
      for (const relation of parentRelationships) {
        if (!memberIds.has(relation.to)) continue;
        const parentCenter = personCenter(relation.from);
        if (parentCenter === null) continue;
        weightedTarget += parentCenter;
        totalWeight += 1;
      }
      return {
        ...block,
        target: totalWeight ? weightedTarget / totalWeight : block.baseCenter + homeShift,
        weight: totalWeight || 4,
      };
    });
    for (const [blockId, center] of packHomeRow(row)) blockCenters.set(blockId, center);
  }

  return baseNodes.map((node) => {
    const block = blockByPerson.get(node.id);
    const center = blockCenters.get(block.id) + block.offsets.get(node.id);
    return { ...node, position: { ...node.position, x: center - nodeCardWidth(node) / 2 } };
  });
}

function generationLabel(generation) {
  if (generation === 1) return "Дети";
  if (generation === 0) return "Текущее поколение";
  if (generation === -1) return "Родители";
  if (generation === -2) return "Бабушки и дедушки";
  if (generation === -3) return "Прабабушки и прадедушки";
  if (generation === -4) return "Прапрабабушки и прапрадедушки";
  if (generation < 0) return `${Math.abs(generation)}-е поколение предков`;
  if (generation === 2) return "Внуки";
  return `${generation}-е поколение потомков`;
}

function getHomeContext(people, relationships, homeId, ancestorDepth) {
  const peopleById = new Map(people.map((person) => [person.id, person]));
  if (!peopleById.has(homeId)) return null;

  const parentsByChild = new Map();
  const childrenByParent = new Map();
  const samePersonById = new Map();
  for (const relation of relationships) {
    if (!peopleById.has(relation.from) || !peopleById.has(relation.to)) continue;
    if (relation.type === "parent") {
      if (!parentsByChild.has(relation.to)) parentsByChild.set(relation.to, new Set());
      if (!childrenByParent.has(relation.from)) childrenByParent.set(relation.from, new Set());
      parentsByChild.get(relation.to).add(relation.from);
      childrenByParent.get(relation.from).add(relation.to);
    } else if (relation.type === "same-person") {
      if (!samePersonById.has(relation.from)) samePersonById.set(relation.from, new Set());
      if (!samePersonById.has(relation.to)) samePersonById.set(relation.to, new Set());
      samePersonById.get(relation.from).add(relation.to);
      samePersonById.get(relation.to).add(relation.from);
    }
  }

  const generationById = new Map();
  const expandIdentity = (seedIds, generation) => {
    const expanded = new Set();
    const queue = [...seedIds];
    while (queue.length) {
      const id = queue.shift();
      if (!peopleById.has(id) || expanded.has(id)) continue;
      const existingGeneration = generationById.get(id);
      if (existingGeneration !== undefined && existingGeneration !== generation) continue;
      if (existingGeneration === undefined) generationById.set(id, generation);
      expanded.add(id);
      for (const aliasId of samePersonById.get(id) || []) queue.push(aliasId);
    }
    return expanded;
  };

  const homeIdentityIds = expandIdentity([homeId], 0);
  let frontier = new Set(homeIdentityIds);
  for (let level = 1; level <= ancestorDepth && frontier.size; level += 1) {
    const parents = new Set();
    for (const childId of frontier) {
      for (const parentId of parentsByChild.get(childId) || []) {
        if (generationById.has(parentId)) continue;
        parents.add(parentId);
      }
    }
    frontier = expandIdentity(parents, -level);
  }

  const addAtGeneration = (id, generation) => {
    expandIdentity([id], generation);
  };

  for (const relation of relationships) {
    if (["partner", "sibling", "same-person"].includes(relation.type) && (homeIdentityIds.has(relation.from) || homeIdentityIds.has(relation.to))) {
      addAtGeneration(homeIdentityIds.has(relation.from) ? relation.to : relation.from, 0);
    }
    if (relation.type === "parent" && homeIdentityIds.has(relation.from)) addAtGeneration(relation.to, 1);
  }

  for (const identityId of homeIdentityIds) {
    for (const parentId of parentsByChild.get(identityId) || []) {
      const parentIdentityIds = new Set();
      const parentQueue = [parentId];
      while (parentQueue.length) {
        const currentParentId = parentQueue.shift();
        if (parentIdentityIds.has(currentParentId)) continue;
        parentIdentityIds.add(currentParentId);
        for (const aliasId of samePersonById.get(currentParentId) || []) parentQueue.push(aliasId);
      }
      for (const currentParentId of parentIdentityIds) {
        for (const siblingId of childrenByParent.get(currentParentId) || []) addAtGeneration(siblingId, 0);
      }
    }
  }

  const homeChildren = [...generationById.entries()].filter(([, generation]) => generation === 1).map(([id]) => id);
  for (const childId of homeChildren) {
    for (const coParentId of parentsByChild.get(childId) || []) addAtGeneration(coParentId, 0);
  }

  const ids = new Set(generationById.keys());
  return {
    people: [...ids].map((id) => ({ ...peopleById.get(id), generation: generationById.get(id) })),
    relationships: relationships.filter((relation) => ids.has(relation.from) && ids.has(relation.to)),
    generationById,
    ancestorDepth,
  };
}

function layoutHomeGraph(people, relationships, homeId) {
  const baseNodes = layoutGraph(people, relationships, { groupSiblingRelationships: false });
  const generations = [...new Set(people.map((person) => person.generation ?? 0))].sort((left, right) => left - right);
  const minimumGeneration = generations[0] ?? 0;
  const nodes = alignHomeFamilyRows(baseNodes.map((node) => {
    const generation = node.data.generation ?? 0;
    return {
      ...node,
      position: { ...node.position, y: (generation - minimumGeneration) * HOME_ROW_STEP },
      data: { ...node.data, generation },
    };
  }), relationships, homeId);

  if (!nodes.length) return { nodes, bands: [] };
  const left = Math.min(...nodes.map((node) => node.position.x)) - 120;
  const right = Math.max(...nodes.map((node) => node.position.x + (node.data.kind === "group" ? GROUP_WIDTH : NODE_WIDTH))) + 120;
  const bands = generations.map((generation) => {
    const centerY = (generation - minimumGeneration) * HOME_ROW_STEP + NODE_HEIGHT / 2;
    return {
      generation,
      label: generationLabel(generation),
      x: left,
      y: centerY - HOME_ROW_STEP / 2,
      width: right - left,
      height: HOME_ROW_STEP,
    };
  });
  return { nodes, bands };
}

function getFocusContext(people, relationships, selectedId) {
  const peopleById = new Map(people.map((person) => [person.id, person]));
  const parents = new Set();
  const children = new Set();
  const partners = new Set();
  const siblings = new Set();
  const others = new Set();

  for (const relation of relationships) {
    if (relation.type === "parent" && relation.to === selectedId) parents.add(relation.from);
    else if (relation.type === "parent" && relation.from === selectedId) children.add(relation.to);
    else if (relation.type === "partner" && (relation.from === selectedId || relation.to === selectedId)) partners.add(relation.from === selectedId ? relation.to : relation.from);
    else if (relation.type === "sibling" && (relation.from === selectedId || relation.to === selectedId)) siblings.add(relation.from === selectedId ? relation.to : relation.from);
    else if (relation.from === selectedId || relation.to === selectedId) others.add(relation.from === selectedId ? relation.to : relation.from);
  }

  for (const relation of relationships) {
    if (relation.type === "parent" && parents.has(relation.from) && relation.to !== selectedId) siblings.add(relation.to);
  }

  for (const id of parents) others.delete(id);
  for (const id of children) others.delete(id);
  for (const id of partners) others.delete(id);
  for (const id of siblings) others.delete(id);

  const ids = new Set([selectedId, ...parents, ...children, ...partners, ...siblings, ...others]);
  const roles = new Map([[selectedId, "Выбранный"]]);
  for (const id of parents) roles.set(id, "Родитель");
  for (const id of children) roles.set(id, "Ребёнок");
  for (const id of partners) roles.set(id, "Супруг / партнёр");
  for (const id of siblings) roles.set(id, "Брат / сестра");
  for (const id of others) roles.set(id, "Другая связь");

  return {
    people: [...ids].map((id) => peopleById.get(id)).filter(Boolean),
    relationships: relationships.filter((relation) => ids.has(relation.from) && ids.has(relation.to)),
    roles,
    counts: { parents: parents.size, siblings: siblings.size, partners: partners.size, children: children.size, others: others.size },
  };
}

function layoutFocusGraph(people, selectedId, roles) {
  const byRole = (role) => people.filter((person) => roles.get(person.id) === role).sort((left, right) => left.name.localeCompare(right.name, "ru"));
  const parents = byRole("Родитель");
  const siblings = byRole("Брат / сестра");
  const partners = byRole("Супруг / партнёр");
  const children = byRole("Ребёнок");
  const others = byRole("Другая связь");
  const positions = new Map();

  function placeCenteredRow(members, y, gap = FOCUS_ROW_GAP) {
    const totalWidth = members.reduce((sum, person) => sum + (person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH), 0) + Math.max(0, members.length - 1) * gap;
    let cursor = -totalWidth / 2;
    for (const person of members) {
      const width = person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH;
      positions.set(person.id, { x: cursor, y });
      cursor += width + gap;
    }
  }

  placeCenteredRow(parents, 0, FOCUS_FAMILY_GAP);
  positions.set(selectedId, { x: -FOCUS_NODE_WIDTH / 2, y: FOCUS_ROW_STEP });

  let leftCursor = -FOCUS_NODE_WIDTH / 2 - FOCUS_ROW_GAP;
  for (const person of [...siblings].reverse()) {
    const width = person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH;
    leftCursor -= width;
    positions.set(person.id, { x: leftCursor, y: FOCUS_ROW_STEP });
    leftCursor -= FOCUS_ROW_GAP;
  }
  let rightCursor = FOCUS_NODE_WIDTH / 2 + FOCUS_ROW_GAP;
  for (const person of partners) {
    positions.set(person.id, { x: rightCursor, y: FOCUS_ROW_STEP });
    rightCursor += (person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH) + FOCUS_ROW_GAP;
  }
  for (const person of others) {
    positions.set(person.id, { x: rightCursor, y: FOCUS_ROW_STEP });
    rightCursor += (person.kind === "group" ? GROUP_WIDTH : NODE_WIDTH) + FOCUS_ROW_GAP;
  }

  placeCenteredRow(children, FOCUS_ROW_STEP * 2, FOCUS_FAMILY_GAP);

  return people.map((person) => {
    const lines = person.label.split("\n");
    return {
      id: person.id,
      type: "family",
      position: positions.get(person.id) || { x: 0, y: FOCUS_ROW_STEP * 3 },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      data: { ...person, focused: person.id === selectedId, contextRole: roles.get(person.id), subtitle: lines.slice(1, 3).join(" · ") },
    };
  });
}

function SourceButtons({ sourceIds, sourceMap, returnHref }) {
  if (!sourceIds?.length) return <span className="no-source">Источник пока не прикреплён</span>;
  return (
    <div className="source-list">
      {sourceIds.map((id) => {
        const source = sourceMap.get(id);
        const href = sourceHref(source, returnHref);
        const isMarkdown = /\.md$/i.test(source?.localPath || "");
        if (!source || !href) return null;
        return <a key={id} href={href} target={isMarkdown ? undefined : "_blank"} rel={isMarkdown ? undefined : "noreferrer"} className={`source-link ${isMarkdown ? "source-markdown" : ""}`}>{source.label}</a>;
      })}
    </div>
  );
}

function PersonPanel({ person, sourceMap, returnHref, onClose, expanded, onToggleExpanded }) {
  const factGroups = useMemo(() => Object.fromEntries(statusOrder.map((status) => [status, person.facts.filter((fact) => fact.status === status)])), [person]);
  const touchStart = useRef(null);

  function handleTouchStart(event) {
    const touch = event.touches[0];
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  function handleTouchEnd(event) {
    const start = touchStart.current;
    const touch = event.changedTouches[0];
    touchStart.current = null;
    if (!start || !touch) return;
    const deltaX = start.x - touch.clientX;
    const deltaY = start.y - touch.clientY;
    const verticalIntent = Math.abs(deltaY) > Math.abs(deltaX) * 1.2;
    if (!verticalIntent) return;
    if (deltaY > 48 && !expanded) onToggleExpanded();
    else if (deltaY < -48 && expanded && event.currentTarget.scrollTop <= 2) onToggleExpanded();
  }

  return (
    <aside className={`detail-panel person-panel ${expanded ? "is-expanded" : "is-collapsed"}`} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <div className="panel-drag-handle" aria-hidden="true" />
      <div className="panel-header">
        <div>
          <div className="eyebrow">{person.kind === "group" ? "Семейная группа" : "Карточка человека"}</div>
          <h2><button className="panel-title-button" onClick={onToggleExpanded} aria-expanded={expanded} title={expanded ? "Свернуть карточку" : "Раскрыть карточку"}>{person.name}</button></h2>
        </div>
        <div className="panel-actions">
          <button className="panel-toggle" onClick={onToggleExpanded}><MaterialIcon name={expanded ? "collapse" : "expand"} size={18} />{expanded ? "Свернуть" : "Подробнее"}</button>
          <button className="icon-button" onClick={onClose} aria-label="Закрыть карточку" title="Закрыть"><MaterialIcon name="close" /></button>
        </div>
      </div>

      {person.media?.length > 0 && (
        <div className="media-strip">
          {person.media.map((item) => <a key={item.id} href={localHref(item.localPath)} target="_blank" rel="noreferrer" className="media-card"><img src={localHref(item.localPath)} alt={item.label} /><span>{item.label}</span></a>)}
        </div>
      )}

      <div className="identity-block">
        {person.label.split("\n").slice(1).map((line) => <div key={line}>{line}</div>)}
        {person.profileId && <span className="profile-id">{person.profileId}</span>}
      </div>

      {factGroups.documented.length > 0 && <FactSection title="Подтверждено документами" facts={factGroups.documented} sourceMap={sourceMap} returnHref={returnHref} />}
      {factGroups.family.length > 0 && <FactSection title="Семейные свидетельства" facts={factGroups.family} sourceMap={sourceMap} returnHref={returnHref} />}
      {(factGroups.strong.length > 0 || factGroups.weak.length > 0) && (
        <section className="fact-section unverified">
          <h3>Непроверенная информация</h3>
          {[...factGroups.strong, ...factGroups.weak].map((fact) => <Fact key={fact.id} fact={fact} sourceMap={sourceMap} returnHref={returnHref} />)}
        </section>
      )}
      {person.openQuestions?.length > 0 && (
        <section className="fact-section open-questions">
          <h3>Открытые вопросы</h3>
          {person.openQuestions.map((question) => <div className="fact open-question" key={question}><p>{question}</p></div>)}
        </section>
      )}
      {factGroups.excluded.length > 0 && <FactSection title="Исключено или опровергнуто" facts={factGroups.excluded} sourceMap={sourceMap} returnHref={returnHref} />}
      {person.facts.length === 0 && <div className="empty-panel">Пока сохранена только краткая подпись из схемы.</div>}
    </aside>
  );
}

function FactSection({ title, facts, sourceMap, returnHref }) {
  return <section className="fact-section"><h3>{title}</h3>{facts.map((fact) => <Fact key={fact.id} fact={fact} sourceMap={sourceMap} returnHref={returnHref} />)}</section>;
}

function Fact({ fact, sourceMap, returnHref }) {
  return (
    <div className="fact">
      <div className="fact-topline"><strong>{fact.label}</strong><span className={`status-pill status-${fact.status}`}>{statusStyle[fact.status]?.label}</span></div>
      <p>{fact.value}</p>
      <SourceButtons sourceIds={fact.sourceIds} sourceMap={sourceMap} returnHref={returnHref} />
    </div>
  );
}

function RelationshipPanel({ relationship, peopleMap, sourceMap, returnHref, onClose }) {
  const from = peopleMap.get(relationship.from);
  const to = peopleMap.get(relationship.to);
  return (
    <aside className="detail-panel relationship-panel">
      <div className="panel-header">
        <div><div className="eyebrow">Карточка связи</div><h2>{relationship.label}</h2></div>
        <button className="icon-button" onClick={onClose} aria-label="Закрыть карточку" title="Закрыть"><MaterialIcon name="close" /></button>
      </div>
      <div className="relation-pair"><div>{from?.name}</div><span>↕</span><div>{to?.name}</div></div>
      <div className="relation-summary">
        <div><span>Тип</span><strong>{relationship.type}</strong></div>
        <div><span>Достоверность</span><strong className={`text-${relationship.status}`}>{statusStyle[relationship.status]?.label}</strong></div>
        <div><span>Направление</span><strong>{relationship.directed ? "от первого ко второму" : "двусторонняя связь"}</strong></div>
      </div>
      <section className="fact-section"><h3>Основания и источники</h3><SourceButtons sourceIds={relationship.sourceIds} sourceMap={sourceMap} returnHref={returnHref} /></section>
    </aside>
  );
}

function Login({ onLoggedIn }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const response = await fetch("/api/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
    const body = await response.json().catch(() => ({}));
    setBusy(false);
    if (!response.ok) return setError(body.error || "Не удалось войти");
    onLoggedIn();
  }
  return (
    <main className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div className="eyebrow">Частный архив</div>
        <h1>Просмотрщик деревьев</h1>
        <p>Доступ к дереву, документам и исследовательским гипотезам защищён общим паролем.</p>
        <label className="text-field"><span>Пароль</span><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus autoComplete="current-password" /></label>
        {error && <div className="login-error">{error}</div>}
        <button className="filled-button" type="submit" disabled={busy || !password}>{busy ? "Проверяю…" : "Открыть дерево"}</button>
      </form>
    </main>
  );
}

function TreeWorkspace({ data, onLogout, onReload }) {
  const { fitView, setCenter } = useReactFlow();
  const initialRoute = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialPersonId = initialRoute.get("person");
  const initialRelationshipId = initialRoute.get("relationship");
  const initialFocusId = initialRoute.get("focus") || initialPersonId;
  const homePersonId = data.people.some((person) => person.id === data.meta?.homePersonId) ? data.meta.homePersonId : null;
  const homeAncestorDepth = Number.isInteger(data.meta?.homeAncestorDepth) ? data.meta.homeAncestorDepth : DEFAULT_HOME_ANCESTOR_DEPTH;
  const validInitialFocusId = data.people.some((person) => person.id === initialFocusId) ? initialFocusId : null;
  const validInitialRelationshipId = data.relationships.some((relationship) => relationship.id === initialRelationshipId) ? initialRelationshipId : null;
  const [viewMode, setViewMode] = useState(() => validInitialFocusId ? "focus" : (initialRoute.get("view") === "all" || validInitialRelationshipId || !homePersonId ? "all" : "home"));
  const [activeStatuses, setActiveStatuses] = useState({ documented: true, family: true, strong: true, weak: false, excluded: false });
  const [selectedPersonId, setSelectedPersonId] = useState(() => data.people.some((person) => person.id === initialPersonId) ? initialPersonId : null);
  const [selectedRelationshipId, setSelectedRelationshipId] = useState(validInitialRelationshipId);
  const [focusPersonId, setFocusPersonId] = useState(validInitialFocusId);
  const [focusHistory, setFocusHistory] = useState([]);
  const [mobilePanelExpanded, setMobilePanelExpanded] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const canvasRef = useRef(null);
  const sourceMap = useMemo(() => new Map(data.sources.map((source) => [source.id, source])), [data]);
  const peopleMap = useMemo(() => new Map(data.people.map((person) => [person.id, person])), [data]);
  const relationshipMap = useMemo(() => new Map(data.relationships.map((relation) => [relation.id, relation])), [data]);
  const visibleRelationships = useMemo(() => data.relationships.filter((relation) => activeStatuses[relation.status]), [data, activeStatuses]);
  const visiblePeople = useMemo(() => {
    const visibleIds = new Set(visibleRelationships.flatMap((relation) => [relation.from, relation.to]));
    const connectedIds = new Set(data.relationships.flatMap((relation) => [relation.from, relation.to]));
    return data.people.filter((person) => visibleIds.has(person.id) || (!connectedIds.has(person.id) && activeStatuses[person.status]));
  }, [data, visibleRelationships, activeStatuses]);
  const homeContext = useMemo(() => viewMode === "home" && homePersonId
    ? getHomeContext(data.people, visibleRelationships, homePersonId, homeAncestorDepth)
    : null, [data.people, visibleRelationships, viewMode, homePersonId, homeAncestorDepth]);
  const focusContext = useMemo(() => viewMode === "focus" && focusPersonId
    ? getFocusContext(data.people, visibleRelationships, focusPersonId)
    : null, [data.people, visibleRelationships, viewMode, focusPersonId]);
  const displayedPeople = homeContext?.people || focusContext?.people || visiblePeople;
  const displayedRelationships = homeContext?.relationships || focusContext?.relationships || visibleRelationships;
  const homeLayout = useMemo(() => homeContext
    ? layoutHomeGraph(displayedPeople, displayedRelationships, homePersonId)
    : { nodes: [], bands: [] }, [homeContext, displayedPeople, displayedRelationships, homePersonId]);
  const nodes = useMemo(() => {
    if (homeContext) return homeLayout.nodes;
    if (focusContext) return layoutFocusGraph(displayedPeople, focusPersonId, focusContext.roles);
    return layoutGraph(displayedPeople, displayedRelationships);
  }, [homeContext, homeLayout.nodes, focusContext, displayedPeople, displayedRelationships, focusPersonId]);
  const generationBands = homeContext ? homeLayout.bands : [];
  const focusPrimaryBounds = useMemo(() => {
    if (!focusContext || !focusPersonId) return null;
    const primaryNodes = nodes.filter((node) => node.id === focusPersonId || focusContext.roles.get(node.id) === "Родитель");
    if (!primaryNodes.length) return null;
    const left = Math.min(...primaryNodes.map((node) => node.position.x));
    const top = Math.min(...primaryNodes.map((node) => node.position.y));
    const right = Math.max(...primaryNodes.map((node) => node.position.x + (node.id === focusPersonId ? FOCUS_NODE_WIDTH : nodeCardWidth(node))));
    const bottom = Math.max(...primaryNodes.map((node) => node.position.y + (node.id === focusPersonId ? 110 : NODE_HEIGHT)));
    return { x: left, y: top, width: right - left, height: bottom - top };
  }, [focusContext, focusPersonId, nodes]);
  const nodePositionMap = useMemo(() => new Map(nodes.map((node) => [node.id, node.position])), [nodes]);
  const edges = useMemo(() => displayedRelationships.map((relation) => {
    const source = nodePositionMap.get(relation.from);
    const target = nodePositionMap.get(relation.to);
    const lateral = relation.type !== "parent";
    const sourceIsLeft = (source?.x || 0) <= (target?.x || 0);
    return {
      id: relation.id, source: relation.from, target: relation.to, type: "evidence", animated: false,
      sourceHandle: lateral ? (sourceIsLeft ? "right-source" : "left-source") : "bottom-source",
      targetHandle: lateral ? (sourceIsLeft ? "left-target" : "right-target") : "top-target",
      markerEnd: relation.directed ? { type: MarkerType.ArrowClosed, color: statusStyle[relation.status]?.color || statusStyle.family.color, width: 14, height: 14 } : undefined,
      data: { status: relation.status, shortLabel: relation.label, onOpen: (id) => { setSelectedPersonId(null); setSelectedRelationshipId(id); } },
    };
  }), [displayedRelationships, nodePositionMap]);
  const matches = useMemo(() => query.trim().length < 2 ? [] : data.people.filter((person) => `${person.name} ${person.label}`.toLowerCase().includes(query.toLowerCase())).slice(0, 8), [data, query]);
  const focusedPerson = focusPersonId ? peopleMap.get(focusPersonId) : null;
  const homePerson = homePersonId ? peopleMap.get(homePersonId) : null;
  const hiddenFocusConnections = useMemo(() => viewMode === "focus" && focusPersonId ? data.relationships.filter((relation) => (relation.from === focusPersonId || relation.to === focusPersonId) && !activeStatuses[relation.status]).length : 0, [data.relationships, viewMode, focusPersonId, activeStatuses]);
  const homeGenerationCount = useMemo(() => homeContext ? new Set(homeContext.generationById.values()).size : 0, [homeContext]);
  const homeAncestorRows = useMemo(() => homeContext ? new Set([...homeContext.generationById.values()].filter((generation) => generation < 0)).size : 0, [homeContext]);
  const returnHref = useMemo(() => {
    const parameters = new URLSearchParams();
    if (viewMode === "all") parameters.set("view", "all");
    if (viewMode === "focus" && focusPersonId) parameters.set("focus", focusPersonId);
    if (selectedPersonId) parameters.set("person", selectedPersonId);
    if (selectedRelationshipId) parameters.set("relationship", selectedRelationshipId);
    const query = parameters.toString();
    return `/${query ? `?${query}` : ""}`;
  }, [viewMode, focusPersonId, selectedPersonId, selectedRelationshipId]);

  useEffect(() => {
    document.title = data.meta?.title || "Просмотрщик семейных деревьев";
  }, [data.meta?.title]);

  useEffect(() => {
    const current = `${window.location.pathname}${window.location.search}`;
    if (current !== returnHref) window.history.replaceState(window.history.state, "", returnHref);
  }, [returnHref]);

  const homeNode = viewMode === "home" ? nodes.find((node) => node.id === homePersonId) : null;
  const fitCurrent = useCallback(() => window.setTimeout(() => {
    const mobile = window.innerWidth <= 760;
    if (viewMode === "home" && homeNode) {
      const width = homeNode.data.kind === "group" ? GROUP_WIDTH : NODE_WIDTH;
      const ancestorOffsetRows = Math.min(mobile ? 0.5 : 2, homeAncestorRows * 0.5);
      setCenter(
        homeNode.position.x + width / 2,
        homeNode.position.y + NODE_HEIGHT / 2 - HOME_ROW_STEP * ancestorOffsetRows,
        { zoom: mobile ? 0.72 : 0.58, duration: 500 },
      );
      return;
    }
    if (viewMode === "focus" && focusPrimaryBounds) {
      const canvasWidth = canvasRef.current?.clientWidth || window.innerWidth;
      const canvasHeight = canvasRef.current?.clientHeight || window.innerHeight;
      const padding = mobile ? 16 : 32;
      const availableWidth = Math.max(240, canvasWidth - padding * 2);
      const availableHeight = Math.max(240, canvasHeight - padding * 2);
      const zoom = Math.max(0.25, Math.min(mobile ? 0.72 : 1.05, availableWidth / focusPrimaryBounds.width, availableHeight / focusPrimaryBounds.height));
      setCenter(
        focusPrimaryBounds.x + focusPrimaryBounds.width / 2,
        focusPrimaryBounds.y + focusPrimaryBounds.height / 2,
        { zoom, duration: 500 },
      );
      return;
    }
    fitView({
      padding: viewMode === "home" ? 0.12 : (mobile ? 0.16 : 0.08),
      duration: 500,
      minZoom: 0.04,
      maxZoom: viewMode === "home" ? 0.8 : (mobile ? 0.7 : 1),
    });
  }, 70), [fitView, setCenter, viewMode, homeNode, homeAncestorRows, focusPrimaryBounds, selectedPersonId]);
  useEffect(() => {
    const timer = fitCurrent();
    return () => window.clearTimeout(timer);
  }, [activeStatuses, viewMode, focusPersonId, displayedPeople.length, fitCurrent]);

  const navigateToPerson = useCallback((personId, remember = true) => {
    if (remember && viewMode === "focus" && focusPersonId && focusPersonId !== personId) setFocusHistory((current) => [...current, focusPersonId].slice(-20));
    setViewMode("focus");
    setFocusPersonId(personId);
    setSelectedRelationshipId(null);
    setSelectedPersonId(personId);
    setMobilePanelExpanded(false);
  }, [viewMode, focusPersonId]);

  function goBack() {
    setFocusHistory((current) => {
      const previous = current.at(-1);
      if (!previous) return current;
      setViewMode("focus");
      setFocusPersonId(previous);
      setSelectedRelationshipId(null);
      setSelectedPersonId(previous);
      setMobilePanelExpanded(false);
      return current.slice(0, -1);
    });
  }

  function showHomeTree() {
    setViewMode(homePersonId ? "home" : "all");
    setFocusPersonId(null);
    setFocusHistory([]);
    setSelectedPersonId(null);
    setSelectedRelationshipId(null);
    setMobilePanelExpanded(false);
  }

  function showWholeTree() {
    setViewMode("all");
    setFocusPersonId(null);
    setFocusHistory([]);
    setSelectedPersonId(null);
    setSelectedRelationshipId(null);
    setMobilePanelExpanded(false);
  }

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key !== "Escape") return;
      if (mobileSearchOpen) {
        setMobileSearchOpen(false);
        setQuery("");
      } else if (viewMode === "focus") {
        if (homePersonId) showHomeTree();
        else showWholeTree();
      } else if (viewMode === "all" && homePersonId) showHomeTree();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [viewMode, homePersonId, mobileSearchOpen]);

  useEffect(() => {
    if (mobileSearchOpen) window.setTimeout(() => searchInputRef.current?.focus(), 40);
  }, [mobileSearchOpen]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // iOS Safari emits native gesture events in addition to touch events.
    // Cancelling only their browser default leaves React Flow's own pinch zoom
    // active while preventing the whole page (and its fixed UI) from scaling.
    const shouldBlockPageGesture = () =>
      window.matchMedia("(pointer: coarse)").matches || window.innerWidth <= 760;

    const preventViewportGesture = (event) => {
      // Safari uses the same gesture events for an iPhone screen and a Mac
      // trackpad. Block browser page zoom only on the mobile/touch-first UI;
      // on desktop React Flow must receive the trackpad pinch normally.
      if (shouldBlockPageGesture() && event.cancelable) event.preventDefault();
    };
    const preventViewportMultiTouch = (event) => {
      if (
        shouldBlockPageGesture() &&
        event.touches?.length > 1 &&
        event.cancelable
      ) {
        event.preventDefault();
      }
    };
    const nonPassive = { passive: false };

    canvas.addEventListener("gesturestart", preventViewportGesture, nonPassive);
    canvas.addEventListener("gesturechange", preventViewportGesture, nonPassive);
    canvas.addEventListener("gestureend", preventViewportGesture, nonPassive);
    canvas.addEventListener("touchmove", preventViewportMultiTouch, nonPassive);
    return () => {
      canvas.removeEventListener("gesturestart", preventViewportGesture, nonPassive);
      canvas.removeEventListener("gesturechange", preventViewportGesture, nonPassive);
      canvas.removeEventListener("gestureend", preventViewportGesture, nonPassive);
      canvas.removeEventListener("touchmove", preventViewportMultiTouch, nonPassive);
    };
  }, []);

  function chooseSearch(person) {
    setQuery("");
    setMobileSearchOpen(false);
    navigateToPerson(person.id);
  }

  function toggleMobileSearch() {
    setMobileSearchOpen((current) => {
      if (current) setQuery("");
      return !current;
    });
  }

  return (
    <main className={`workspace ${mobileSearchOpen ? "search-open" : ""} ${selectedPersonId || selectedRelationshipId ? "has-detail" : ""} ${selectedPersonId && !mobilePanelExpanded ? "has-collapsed-person" : ""} ${mobilePanelExpanded ? "detail-expanded" : ""} ${viewMode === "focus" ? "is-focus-mode" : ""} ${viewMode === "home" ? "is-home-mode" : ""}`}>
      <header className="topbar">
        <div className="brand"><div><strong>{data.meta?.title || "Семейное дерево"}</strong><span>{viewMode === "home" ? `Домашнее дерево · ${displayedPeople.length} карточек · ${homeGenerationCount} поколений` : (viewMode === "focus" ? `Семья в фокусе · ${displayedPeople.length} карточки` : `${visiblePeople.length} узлов · ${visibleRelationships.length} связей`)}</span></div></div>
        <div className="search-wrap">
          <MaterialIcon name="search" size={19} />
          <input ref={searchInputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти человека…" aria-label="Поиск человека" />
          {matches.length > 0 && <div className="search-results">{matches.map((person) => <button key={person.id} onClick={() => chooseSearch(person)}><strong>{person.name}</strong><span>{person.label.split("\n").slice(1, 2)}</span></button>)}</div>}
        </div>
        <div className="top-actions">
          <button className="top-icon-button search-toggle" onClick={toggleMobileSearch} aria-expanded={mobileSearchOpen} aria-label={mobileSearchOpen ? "Закрыть поиск" : "Открыть поиск"} title={mobileSearchOpen ? "Закрыть поиск" : "Поиск"}><MaterialIcon name={mobileSearchOpen ? "close" : "search"} /></button>
          <button className="top-icon-button reload-button" onClick={onReload} aria-label="Обновить данные" title="Обновить данные"><MaterialIcon name="refresh" /></button>
          <button className="top-icon-button" onClick={onLogout} aria-label="Выйти" title="Выйти"><MaterialIcon name="logout" /></button>
        </div>
      </header>

      <div className="filterbar">
        <div className="filter-list">
          <span>Показывать связи:</span>
          {statusOrder.map((status) => <label key={status} className={`filter filter-${status}`}><input type="checkbox" checked={activeStatuses[status]} onChange={() => setActiveStatuses((current) => ({ ...current, [status]: !current[status] }))} /><i>✓</i><span>{statusStyle[status].label}</span></label>)}
        </div>
      </div>

      <section ref={canvasRef} className="canvas">
        {data.people.length === 0 && (
          <div className="canvas-empty">
            <strong>Дерево пока не подключено</strong>
            <span>Укажите файл данных через TREE_VIEWER_DATA_FILE.</span>
          </div>
        )}
        {homeContext && homePerson && (
          <nav className="focus-toolbar home-toolbar" aria-label="Навигация по домашнему дереву">
            <div className="focus-summary">
              <strong>{homePerson.name}</strong>
              <span>До {homeAncestorDepth} поколений предков · ближайшая семья</span>
            </div>
            <button onClick={showWholeTree}><MaterialIcon name="fit" size={18} />Вся база</button>
          </nav>
        )}
        {focusContext && focusedPerson && (
          <nav className="focus-toolbar" aria-label="Навигация по ближайшей семье">
            {focusHistory.length > 0 && <button onClick={goBack} aria-label="Вернуться к предыдущему человеку"><MaterialIcon name="back" size={18} />Назад</button>}
            {homePersonId && <button onClick={showHomeTree}><MaterialIcon name="home" size={18} />Домой</button>}
            <div className="focus-summary">
              <strong>{focusedPerson.name}</strong>
              <span>Родители: {focusContext.counts.parents} · Братья/сёстры: {focusContext.counts.siblings} · Партнёры: {focusContext.counts.partners} · Дети: {focusContext.counts.children}{hiddenFocusConnections ? ` · Скрыто связей: ${hiddenFocusConnections}` : ""}</span>
            </div>
            <button onClick={showWholeTree}><MaterialIcon name="fit" size={18} />Вся база</button>
          </nav>
        )}
        {viewMode === "all" && homePerson && (
          <nav className="focus-toolbar all-tree-toolbar" aria-label="Навигация по всей базе">
            <button onClick={showHomeTree}><MaterialIcon name="home" size={18} />Домашнее дерево</button>
            <div className="focus-summary">
              <strong>Вся база</strong>
              <span>{visiblePeople.length} узлов · {visibleRelationships.length} связей</span>
            </div>
          </nav>
        )}
        <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes} onNodeClick={(_, node) => navigateToPerson(node.id)} onEdgeClick={(_, edge) => { setSelectedPersonId(null); setSelectedRelationshipId(edge.id); }} fitView={viewMode === "all"} minZoom={0.03} maxZoom={2.4} nodesDraggable={false} nodesConnectable={false} elementsSelectable panOnDrag panOnScroll zoomOnScroll={false} zoomOnPinch zoomOnDoubleClick={false} preventScrolling selectionOnDrag={false} proOptions={{ hideAttribution: true }}>
          <Background color="#c2c8c1" gap={28} size={1} />
          <GenerationBands bands={generationBands} />
          {nodes.length > 0 && <Controls showInteractive={false} />}
          {nodes.length > 0 && <MiniMap nodeColor={(node) => branchColor(node.data.branch)} maskColor="rgba(241, 244, 239, 0.82)" pannable zoomable />}
        </ReactFlow>
      </section>

      {selectedPersonId && peopleMap.get(selectedPersonId) && <PersonPanel person={peopleMap.get(selectedPersonId)} sourceMap={sourceMap} returnHref={returnHref} expanded={mobilePanelExpanded} onToggleExpanded={() => setMobilePanelExpanded((current) => !current)} onClose={() => setSelectedPersonId(null)} />}
      {selectedRelationshipId && relationshipMap.get(selectedRelationshipId) && <RelationshipPanel relationship={relationshipMap.get(selectedRelationshipId)} peopleMap={peopleMap} sourceMap={sourceMap} returnHref={returnHref} onClose={() => setSelectedRelationshipId(null)} />}
    </main>
  );
}

function App() {
  const [tree, setTree] = useState(null);
  const [authState, setAuthState] = useState("checking");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    const response = await fetch("/api/tree", { cache: "no-store" });
    if (response.status === 401) { setAuthState("login"); setTree(null); return; }
    const body = await response.json().catch(() => ({}));
    if (!response.ok) { setError(body.error || "Не удалось загрузить дерево"); return; }
    setTree(body);
    setAuthState("ready");
  }, []);

  useEffect(() => { load(); }, [load]);
  async function logout() { await fetch("/api/logout", { method: "POST" }); setTree(null); setAuthState("login"); }
  if (authState === "checking") return <div className="loading"><span className="progress-ring" />Открываю просмотрщик…</div>;
  if (authState === "login") return <Login onLoggedIn={load} />;
  if (error) return <div className="loading error-state"><strong>{error}</strong><button onClick={load}>Повторить</button></div>;
  if (!tree) return <div className="loading"><span className="progress-ring" />Загружаю дерево…</div>;
  if (window.location.pathname === "/markdown") {
    return <Suspense fallback={<div className="loading"><span className="progress-ring" />Открываю просмотрщик…</div>}><MarkdownViewer data={tree} /></Suspense>;
  }
  return <ReactFlowProvider><TreeWorkspace data={tree} onLogout={logout} onReload={load} /></ReactFlowProvider>;
}

createRoot(document.getElementById("root")).render(<React.StrictMode><App /></React.StrictMode>);
